// Market-aware health: what is a real problem and what is just a closed market (2026-10-04, UX review).
// The desks used to show "latest bar 2d ago" and "daily bars 2d stale" in amber every weekend. A warning that fires on a
// normal state trains people to ignore warnings, so each rule below fires only when the state is abnormal FOR THE SESSION.
// Pure functions (no React, no network); tested by frontend/tests/health.test.mjs. Holidays are not modelled: on a weekday
// holiday the daily-bars rule can fire once; thresholds for the watchdog live in shared/watchdog_policy.py (not copied here).

export function etParts(date = new Date()) {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
    weekday: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  })
  const p = Object.fromEntries(f.formatToParts(date).map(x => [x.type, x.value]))
  return { date: `${p.year}-${p.month}-${p.day}`, weekday: p.weekday, hour: Number(p.hour) % 24, minute: Number(p.minute) }
}

// The date the latest daily bar should have by now: the most recent weekday whose 16:30 ET end-of-day job has run.
export function expectedDailyBarDate(now = new Date()) {
  const e = etParts(now)
  let d = new Date(`${e.date}T12:00:00Z`)
  const weekday = !['Sat', 'Sun'].includes(e.weekday)
  const afterJob = e.hour > 16 || (e.hour === 16 && e.minute >= 30)
  if (!(weekday && afterJob)) d = new Date(d.getTime() - 86400000)
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) d = new Date(d.getTime() - 86400000)
  return d.toISOString().slice(0, 10)
}

export function dailyBarsStale(health, now = new Date()) {
  const latest = health && health.daily_bars_latest
  return Boolean(latest) && String(latest).slice(0, 10) < expectedDailyBarDate(now)
}

const IGNORED_WORKERS = /^(asyncio|ThreadPool)/

// Returns [{key, sev: 'critical'|'warning', title, why}] for real problems only.
export function healthIssues({ health, ws, now = new Date() } = {}) {
  const out = []
  if (!health) return out
  const session = health.market_session
  if (health.scheduler_alive === false) {
    // Linux cutover 2026-11-01, Doc 50: wording no longer assumes Railway (T-323)
    out.push({ key: 'scheduler', sev: 'critical', title: 'The trading engine has stopped', why: 'No data collection, signals or stop checks are running. Restart the trading engine (Linux: the saferfunds_engine container; Railway only if rolled back).' })
  }
  if (health.watchdog_alive === false && health.scheduler_alive !== false) {
    out.push({ key: 'watchdog', sev: 'critical', title: 'The engine watchdog has stopped', why: 'If the engine fails it will not restart by itself.' })
  }
  for (const [name, w] of Object.entries(health.workers || {})) {
    if (!IGNORED_WORKERS.test(name) && w && w.last_ok === false) {
      out.push({ key: `worker:${name}`, sev: 'warning', title: `${name} failed its last run`, why: w.last_error ? String(w.last_error).slice(0, 140) : 'See the error log.' })
    }
  }
  if (ws && session !== 'closed' && (ws.status === 'FAILED' || ws.status === 'DEGRADED')) {
    out.push({ key: 'stream', sev: 'warning', title: ws.status === 'FAILED' ? 'Live price stream is down' : 'Live price stream is unstable', why: 'Prices may be delayed until it recovers.' })
  }
  if (health.data_collection_alert && session === 'regular') {
    out.push({ key: 'data', sev: 'critical', title: 'Price data has stalled', why: String(health.data_collection_alert) })
  }
  if (dailyBarsStale(health, now)) {
    out.push({ key: 'daily', sev: 'warning', title: 'Daily price history is behind', why: `Latest daily bar is ${health.daily_bars_latest}; the end-of-day job should have added today's.` })
  }
  return out
}
